import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
/* eslint-disable jsdoc/require-jsdoc -- 页面内私有展示组件由命名、类型和页面测试约束。 */
import {
  Button,
  Drawer,
  Empty,
  Image,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Eye,
  ImagePlus,
  MapPin,
  Pencil,
  Plus,
  PowerOff,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { canUsePermission, PermissionGate } from "../../../lib/auth";
import { getCityOptions, getDistrictOptions, getProvinceOptions } from "../../../shared/region";
import {
  GeoOperationPanel,
  type GeoBusinessVerifyResult,
  type GeoOperationStatus,
} from "../../../shared/business-geo";
import { useCurrentAuthUser } from "../../auth";
import { stayProcurementChannelOptions, stayResourcePermissions } from "../config";
import { groupStayResourceMediaBySku } from "../service";
import { useStayResourceManagementRuntime, type StayResourceManagementRuntime } from "../runtime";
import type {
  StayResourceGeoCandidate,
  StayResourceGeoReviewAuditItem,
  StayResourceLegacyMappingItem,
  StayResourceMediaItem,
  StayResourceSkuItem,
  StayResourceSpuItem,
  StayResourceSupplierItem,
} from "../types";
import {
  StayResourceMediaDrawer,
  StayResourceSkuDrawer,
  StayResourceSpuDrawer,
  StayResourceSupplierDrawer,
  type MediaDrawerState,
  type SkuDrawerState,
  type SpuDrawerState,
  type SupplierDrawerState,
} from "./StayResourceDrawers";
import { StayResourceGeoCandidateMap } from "./StayResourceGeoCandidateMap";
import { StayExternalImportDrawer } from "./StayExternalImportDrawer";
import { StayExternalRecords } from "./StayExternalRecords";
import "./stayResourceManagement.css";
import "../../../shared/ui/crmListPageLayout.css";

const MEDIA_SCENES: Record<string, string> = {
  cover: "封面",
  room: "房型图片",
  entrance: "入口",
  lobby: "大堂",
  surroundings: "周边",
  restaurant: "餐厅",
  non_team: "非团队区域",
  dining: "用餐",
  other: "其他",
};

const GEO_STATUS: Record<string, { label: string; color: string }> = {
  unverified: { label: "未核验", color: "gray" },
  pending: { label: "核验中", color: "blue" },
  resolved: { label: "已定位", color: "green" },
  review_required: { label: "待审核", color: "orange" },
  rejected: { label: "已驳回", color: "red" },
  failed: { label: "核验失败", color: "red" },
};

type DetailTabKey = "hotel" | "legacy" | "external" | "rooms" | "media" | "geo" | "suppliers";

/** 房资源管理的酒店列表与 R1-R5 运营详情页。 */
export function StayResourceManagementFeaturePage() {
  const { spuId } = useParams<{ spuId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const runtime = useStayResourceManagementRuntime(spuId);
  const currentUser = useCurrentAuthUser().data;
  const canDelete = canUsePermission(currentUser, stayResourcePermissions.delete);
  const canManage = canUsePermission(currentUser, stayResourcePermissions.manage);
  const canManageMedia = canUsePermission(currentUser, stayResourcePermissions.manageMedia);
  const canReviewGeo = canUsePermission(currentUser, stayResourcePermissions.reviewGeo);
  const canManageCapability = canUsePermission(
    currentUser,
    stayResourcePermissions.manageCapability,
  );
  const [spuDrawer, setSpuDrawer] = useState<SpuDrawerState | null>(null);
  const [externalImportOpen, setExternalImportOpen] = useState(false);
  const [skuDrawer, setSkuDrawer] = useState<SkuDrawerState | null>(null);
  const [mediaDrawer, setMediaDrawer] = useState<MediaDrawerState | null>(null);
  const [supplierDrawer, setSupplierDrawer] = useState<SupplierDrawerState | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTabKey>("hotel");
  const openedGeoEditRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!spuId || new URLSearchParams(location.search).get("geoEdit") !== "1") return;
    if (!runtime.detail || openedGeoEditRef.current === spuId) return;
    openedGeoEditRef.current = spuId;
    setSpuDrawer({ type: "edit", item: runtime.detail });
  }, [location.search, runtime.detail, spuId]);

  function openDetail(item: StayResourceSpuItem, tab: DetailTabKey) {
    setDetailTab(tab);
    navigate(`/resources/stays/${item.staySpuId}`);
  }

  const columns: TableColumnProps<StayResourceSpuItem>[] = [
    {
      title: "酒店名称",
      dataIndex: "hotelName",
      width: 240,
      render: (_, item) => (
        <div className="stay-resource-name stay-resource-name--wrap">
          <button
            type="button"
            className="stay-resource-link stay-resource-name-link"
            aria-label={`打开酒店资料 ${item.hotelName}`}
            onClick={() => openDetail(item, "hotel")}
          >
            {item.hotelName}
          </button>
          {item.hotelShortName ? <span>{item.hotelShortName}</span> : null}
        </div>
      ),
    },
    {
      title: "地区",
      width: 240,
      render: (_, item) => (
        <div className="stay-resource-location">
          <div>{regionText(item)}</div>
          {item.lng !== undefined && item.lat !== undefined ? (
            <div className="stay-resource-location__coords">
              {item.lng.toFixed(6)}, {item.lat.toFixed(6)}
            </div>
          ) : null}
        </div>
      ),
    },
    { title: "住宿档次", dataIndex: "stayLevel", width: 120, ellipsis: true },
    { title: "酒店类型", dataIndex: "hotelType", width: 140, ellipsis: true },
    {
      title: "房型 / 图片 / 供应商",
      width: 220,
      align: "center",
      render: (_, item) => (
        <div className="stay-resource-count-links">
          <CountLink label="房型" count={item.skuCount} onClick={() => openDetail(item, "rooms")} />
          <CountLink label="图片" count={item.mediaCount} onClick={() => openDetail(item, "media")} />
          <CountLink label="供应商" count={item.supplierCount} onClick={() => openDetail(item, "suppliers")} />
        </div>
      ),
    },
    {
      title: "GEO",
      width: 126,
      render: (_, item) => (
        <GeoStatusAction
          item={item}
          canReview={canReviewGeo}
          pending={runtime.geoMutationPending}
          onOpen={() => openDetail(item, "geo")}
          onVerify={() => runtime.verifyGeo(item.staySpuId)}
        />
      ),
    },
    {
      title: "状态",
      width: 96,
      render: (_, item) => (
        <ResourceStatusControl
          item={item}
          canManage={canManage}
          pending={runtime.mutationPending}
          onToggle={(disabled) => runtime.toggleSpu(item.staySpuId, disabled, false)}
        />
      ),
    },
    { title: "更新时间", width: 170, render: (_, item) => formatDateTime(item.updatedAt) },
    {
      title: "操作",
      width: canManage || canDelete ? 120 : 72,
      fixed: "right",
      align: "center",
      render: (_, item) => (
        <div className="stay-resource-actions">
          <IconButton label={`查看 ${item.hotelName}`} icon={<Eye size={16} />} onClick={() => openDetail(item, "hotel")} />
          {canManage ? (
            <IconButton label={`编辑 ${item.hotelName}`} icon={<Pencil size={16} />} onClick={() => setSpuDrawer({ type: "edit", item })} />
          ) : null}
          {canDelete ? (
            <Popconfirm
              position="left"
              getPopupContainer={() => document.body}
              title={`确认删除“${item.hotelName}”？删除后不可恢复。`}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ status: "danger" }}
              onOk={() => runtime.deleteSpu(item.staySpuId)}
            >
              <span className="stay-resource-delete-trigger">
                <IconButton label={`删除 ${item.hotelName}`} icon={<Trash2 size={16} />} status="danger" onClick={() => undefined} />
              </span>
            </Popconfirm>
          ) : null}
        </div>
      ),
    },
  ];

  const cityOptions = runtime.draftFilters.province
    ? getCityOptions(runtime.draftFilters.province)
    : [];
  const districtOptions = runtime.draftFilters.province && runtime.draftFilters.city
    ? getDistrictOptions(runtime.draftFilters.province, runtime.draftFilters.city)
    : [];

  return (
    <main className="stay-resource-page">
      <section
        className="crm-list-filter-panel stay-resource-filter"
        role="search"
        aria-label="房资源筛选"
        data-layout="two-tier-responsive"
      >
        <div className="crm-list-filter-fields stay-resource-filter__fields">
          <div className="crm-list-filter-primary">
            <label className="crm-list-field crm-list-field--lg">
              <span>关键词</span>
              <Input
                allowClear
                value={runtime.draftFilters.keyword ?? ""}
                placeholder="酒店名称或地址"
                onChange={(keyword) => runtime.setDraftFilters((current) => ({ ...current, keyword }))}
                onPressEnter={runtime.search}
              />
            </label>
            <div className="crm-list-field crm-list-field--md">
              <span>省份</span>
              <Select
                allowClear
                showSearch
                aria-label="省份筛选"
                data-testid="stay-resource-province-filter"
                getPopupContainer={filterPopupContainer}
                placeholder="全部省份"
                options={getProvinceOptions().map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.province}
                onChange={(province) => runtime.setDraftFilters((current) => ({
                  ...current,
                  province: province || undefined,
                  city: undefined,
                  district: undefined,
                }))}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>城市</span>
              <Select
                allowClear
                showSearch
                aria-label="城市筛选"
                data-testid="stay-resource-city-filter"
                disabled={!runtime.draftFilters.province}
                getPopupContainer={filterPopupContainer}
                placeholder="全部城市"
                options={cityOptions.map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.city}
                onChange={(city) => runtime.setDraftFilters((current) => ({
                  ...current,
                  city: city || undefined,
                  district: undefined,
                }))}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>区县</span>
              <Select
                allowClear
                showSearch
                aria-label="区县筛选"
                data-testid="stay-resource-district-filter"
                disabled={!runtime.draftFilters.city}
                getPopupContainer={filterPopupContainer}
                placeholder="全部区县"
                options={districtOptions.map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.district}
                onChange={(district) => runtime.setDraftFilters((current) => ({
                  ...current,
                  district: district || undefined,
                }))}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>GEO 状态</span>
              <Select
                allowClear
                value={runtime.draftFilters.geoStatus}
                placeholder="GEO 状态"
                options={Object.entries(GEO_STATUS).map(([value, item]) => ({ value, label: item.label }))}
                onChange={(geoStatus) => runtime.setDraftFilters((current) => ({ ...current, geoStatus: geoStatus || undefined }))}
              />
            </div>
            <div className="crm-list-field crm-list-field--sm">
              <span>启停状态</span>
              <Select
                allowClear
                value={runtime.draftFilters.isDisabled === undefined ? undefined : runtime.draftFilters.isDisabled ? "disabled" : "enabled"}
                placeholder="启停状态"
                options={[{ label: "启用", value: "enabled" }, { label: "停用", value: "disabled" }]}
                onChange={(isDisabled) => runtime.setDraftFilters((current) => ({
                  ...current,
                  isDisabled: isDisabled === "disabled" ? true : isDisabled === "enabled" ? false : undefined,
                }))}
              />
            </div>
          </div>
        </div>
        <div className="crm-list-filter-actions stay-resource-filter__actions">
          <Button aria-label="重置筛选" icon={<RotateCcw size={15} />} onClick={runtime.reset}>
            重置
          </Button>
          <Button type="primary" icon={<Search size={15} />} loading={runtime.fetching} onClick={runtime.search}>
            查询
          </Button>
        </div>
      </section>

      <section className="stay-resource-table" aria-label="酒店资源列表">
        <header className="stay-resource-table__header">
          <div><span>共 {runtime.total} 家酒店</span></div>
          <PermissionGate user={currentUser} permission={stayResourcePermissions.manage}>
            <Button onClick={() => setExternalImportOpen(true)}>导入外部酒店</Button>
            <Button type="primary" icon={<Plus size={16} />} onClick={() => setSpuDrawer({ type: "create" })}>新增酒店</Button>
          </PermissionGate>
        </header>
        {runtime.error ? <div className="stay-resource-error" role="alert">{runtime.error instanceof Error ? runtime.error.message : "房资源加载失败"}</div> : null}
        <Table<StayResourceSpuItem>
          rowKey="staySpuId"
          size="small"
          border={{ wrapper: true, cell: false }}
          data={runtime.items}
          columns={columns}
          loading={runtime.loading}
          pagination={false}
          scroll={{ x: 1390 }}
          tableLayoutFixed
          noDataElement="暂无房资源数据"
        />
        {runtime.total > 0 ? (
          <div className="stay-resource-pagination">
            <Pagination
              current={runtime.page}
              pageSize={runtime.pageSize}
              total={runtime.total}
              sizeCanChange
              sizeOptions={[20, 50, 100]}
              showTotal
              onChange={(page, pageSize) => runtime.changePage(page, pageSize)}
              onPageSizeChange={(pageSize) => runtime.changePage(1, pageSize)}
            />
          </div>
        ) : null}
      </section>

      <StayResourceDetailDrawer
        visible={Boolean(spuId)}
        runtime={runtime}
        activeTab={detailTab}
        canManage={canManage}
        canManageMedia={canManageMedia}
        canDelete={canDelete}
        canReviewGeo={canReviewGeo}
        canManageCapability={canManageCapability}
        onEditSpu={(item) => setSpuDrawer({ type: "edit", item })}
        onCreateSku={(spu) => setSkuDrawer({ type: "create", spu })}
        onEditSku={(spu, item) => setSkuDrawer({ type: "edit", spu, item })}
        onCreateMedia={(spu) => setMediaDrawer({ type: "create", spu })}
        onEditMedia={(spu, item) => setMediaDrawer({ type: "edit", spu, item })}
        onCreateSupplier={(spu) => setSupplierDrawer({ type: "create", spu })}
        onEditSupplier={(spu, item) => setSupplierDrawer({ type: "edit", spu, item })}
        onTabChange={setDetailTab}
        onClose={() => {
          setDetailTab("hotel");
          navigate("/resources/stays");
        }}
      />
      {externalImportOpen && canManage && <StayExternalImportDrawer onClose={() => setExternalImportOpen(false)} />}
      <StayResourceSpuDrawer
        state={spuDrawer}
        pending={runtime.mutationPending}
        errorMessage={runtime.mutationErrorMessage}
        housLevelOptions={runtime.housLevelOptions}
        housLevelOptionsLoading={runtime.housLevelOptionsLoading}
        onClose={() => setSpuDrawer(null)}
        onCreateAndVerifyGeo={runtime.createSpuAndVerifyGeo}
        onUpdate={runtime.updateSpu}
      />
      <StayResourceSkuDrawer state={skuDrawer} pending={runtime.mutationPending} errorMessage={runtime.mutationErrorMessage} onClose={() => setSkuDrawer(null)} onCreate={runtime.createSku} onUpdate={runtime.updateSku} />
      <StayResourceMediaDrawer state={mediaDrawer} skus={runtime.skus} pending={runtime.mutationPending} errorMessage={runtime.mutationErrorMessage} onClose={() => setMediaDrawer(null)} onUpload={runtime.uploadMedia} onCreate={runtime.createMedia} onUpdate={runtime.updateMedia} />
      <StayResourceSupplierDrawer state={supplierDrawer} skus={runtime.skus} pending={runtime.mutationPending} errorMessage={runtime.mutationErrorMessage} onClose={() => setSupplierDrawer(null)} onSearchSuppliers={runtime.searchSupplierOptions} onCreate={runtime.createSupplier} onUpdate={runtime.updateSupplier} />
    </main>
  );
}

type DetailDrawerProps = {
  visible: boolean;
  runtime: StayResourceManagementRuntime;
  activeTab: DetailTabKey;
  canManage: boolean;
  canManageMedia: boolean;
  canDelete: boolean;
  canReviewGeo: boolean;
  canManageCapability: boolean;
  onEditSpu: (item: StayResourceSpuItem) => void;
  onCreateSku: (spu: StayResourceSpuItem) => void;
  onEditSku: (spu: StayResourceSpuItem, item: StayResourceSkuItem) => void;
  onCreateMedia: (spu: StayResourceSpuItem) => void;
  onEditMedia: (spu: StayResourceSpuItem, item: StayResourceMediaItem) => void;
  onCreateSupplier: (spu: StayResourceSpuItem) => void;
  onEditSupplier: (spu: StayResourceSpuItem, item: StayResourceSupplierItem) => void;
  onTabChange: (tab: DetailTabKey) => void;
  onClose: () => void;
};

function StayResourceDetailDrawer(props: DetailDrawerProps) {
  const detail = props.runtime.detail;
  return (
    <Drawer className="stay-resource-drawer" width="min(1080px, 94vw)" visible={props.visible} title={detail?.hotelName ?? "房资源详情"} footer={null} unmountOnExit onCancel={props.onClose}>
      {props.runtime.detailLoading || !detail ? (
        <div className="stay-resource-drawer__loading"><Spin /></div>
      ) : (
        <Tabs
          activeTab={props.activeTab}
          destroyOnHide={false}
          onChange={(key) => props.onTabChange(key as DetailTabKey)}
        >
          <Tabs.TabPane key="hotel" title="酒店资料">
            <SectionHeader title="酒店主档" action={props.canManage ? (
              <div className="stay-resource-actions">
                <Button size="small" icon={<Pencil size={15} />} onClick={() => props.onEditSpu(detail)}>编辑</Button>
              </div>
            ) : null} />
            <HotelFacts item={detail} />
          </Tabs.TabPane>
          <Tabs.TabPane key="legacy" title={`旧系统追溯 ${props.runtime.legacyMappings.length}`}>
            <LegacyMappingTable runtime={props.runtime} />
          </Tabs.TabPane>
          <Tabs.TabPane key="external" title="外部来源">
            <StayExternalRecords spuId={detail.staySpuId} active={props.activeTab === "external"} />
          </Tabs.TabPane>
          <Tabs.TabPane key="rooms" title={`房型 ${props.runtime.skus.length}`}>
            <SkuTable detail={detail} runtime={props.runtime} canManage={props.canManage} onCreate={props.onCreateSku} onEdit={props.onEditSku} />
          </Tabs.TabPane>
          <Tabs.TabPane key="media" title={`图片与附件 ${props.runtime.media.length}`}>
            <MediaManager canDelete={props.canDelete} detail={detail} runtime={props.runtime} canManage={props.canManageMedia} onCreate={props.onCreateMedia} onEdit={props.onEditMedia} />
          </Tabs.TabPane>
          <Tabs.TabPane key="geo" title="地图与 GEO">
            <GeoReviewPanel detail={detail} runtime={props.runtime} canReview={props.canReviewGeo} />
          </Tabs.TabPane>
          <Tabs.TabPane key="suppliers" title={`供应关系 ${props.runtime.suppliers.length}`}>
            <SupplierManager detail={detail} runtime={props.runtime} canManage={props.canManageCapability} onCreate={props.onCreateSupplier} onEdit={props.onEditSupplier} />
          </Tabs.TabPane>
        </Tabs>
      )}
    </Drawer>
  );
}

function LegacyMappingTable({ runtime }: { runtime: StayResourceManagementRuntime }) {
  const columns: TableColumnProps<StayResourceLegacyMappingItem>[] = [
    { title: "来源系统", dataIndex: "sourceSystem", width: 150, ellipsis: true },
    { title: "来源表", dataIndex: "sourceTable", width: 180, ellipsis: true },
    { title: "旧 ID", dataIndex: "legacyId", width: 200, ellipsis: true },
    {
      title: "审核状态",
      width: 110,
      render: (_, item) => (
        <Tag color={item.reviewStatus === "approved" ? "green" : item.reviewStatus === "rejected" ? "red" : "orange"}>
          {legacyReviewStatusLabel(item.reviewStatus)}
        </Tag>
      ),
    },
    { title: "更新时间", width: 170, render: (_, item) => formatDateTime(item.updatedAt) },
  ];
  return (
    <>
      <SectionHeader title="来源映射" />
      {runtime.legacyMappingsUnavailable ? (
        <div className="stay-resource-error" role="alert">旧系统追溯记录加载失败，请稍后重试</div>
      ) : (
        <Table<StayResourceLegacyMappingItem>
          rowKey="mappingId"
          size="small"
          loading={runtime.legacyMappingsLoading}
          data={runtime.legacyMappings}
          columns={columns}
          pagination={false}
          noDataElement="暂无旧系统追溯记录"
          scroll={{ x: 810 }}
        />
      )}
    </>
  );
}

function HotelFacts({ item }: { item: StayResourceSpuItem }) {
  return (
    <dl className="stay-resource-facts">
      <Fact label="酒店全称" value={item.hotelName} />
      <Fact label="酒店简称" value={item.hotelShortName} />
      <Fact label="住宿档次" value={item.stayLevel} />
      <Fact label="酒店类型" value={item.hotelType} />
      <Fact label="公开寻源电话" value={item.publicPhones?.join("、")} />
      <Fact label="房间总数" value={item.roomCount?.toString()} />
      <Fact label="开业年份" value={item.openingYear?.toString()} />
      <Fact label="装修年份" value={item.renovationYear?.toString()} />
      <Fact label="平台评分" value={item.platformRating?.toString()} />
      <Fact label="国家或地区" value={item.country} />
      <Fact label="行政区域" value={locationText(item)} />
      <Fact label="详细地址" value={item.address} wide />
      <Fact label="状态" value={item.isDisabled ? "停用" : item.status} />
      <Fact label="更新时间" value={formatDateTime(item.updatedAt)} />
      <Fact label="创建人 / 更新人" value={`${item.createdBy} / ${item.updatedBy}`} />
      <Fact label="备注" value={item.remark} wide />
    </dl>
  );
}

function isPreviewableImageUrl(fileUrl: string): boolean {
  return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(fileUrl);
}

function SkuRoomImagesCell({
  skuName,
  media,
}: {
  skuName: string;
  media: StayResourceMediaItem[];
}) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const images = media
    .filter((item) => item.mediaStatus !== "obsolete" && isPreviewableImageUrl(item.fileUrl))
    .sort((left, right) => left.sortNo - right.sortNo);
  if (!images.length) {
    return <span className="stay-resource-sku-images__empty">暂无图片</span>;
  }
  return (
    <div className="stay-resource-sku-images">
      <div className="stay-resource-sku-images__thumbs">
        <Image.PreviewGroup
          infinite
          visible={previewVisible}
          onVisibleChange={setPreviewVisible}
        >
          {images.map((item, index) => (
            <Image
              key={item.stayMediaId}
              src={resolveHttpResourceUrl(item.fileUrl)}
              alt={item.mediaName}
              width={40}
              height={32}
              className={index >= 3 ? "stay-resource-sku-images__thumb--hidden" : undefined}
            />
          ))}
        </Image.PreviewGroup>
      </div>
      <Button
        type="text"
        size="mini"
        aria-label={`查看房型图片 ${skuName}`}
        onClick={() => setPreviewVisible(true)}
      >
        查看{images.length}
      </Button>
    </div>
  );
}

function SkuTable({ detail, runtime, canManage, onCreate, onEdit }: {
  detail: StayResourceSpuItem;
  runtime: StayResourceManagementRuntime;
  canManage: boolean;
  onCreate: (spu: StayResourceSpuItem) => void;
  onEdit: (spu: StayResourceSpuItem, item: StayResourceSkuItem) => void;
}) {
  const mediaBySku = runtime.media.reduce<Record<string, StayResourceMediaItem[]>>((acc, item) => {
    if (!item.staySkuId || item.mediaStatus === "obsolete") return acc;
    (acc[item.staySkuId] ??= []).push(item);
    return acc;
  }, {});
  const columns: TableColumnProps<StayResourceSkuItem>[] = [
    { title: "房型名称", dataIndex: "skuName", width: 190, ellipsis: true },
    { title: "物理房型", dataIndex: "roomType", width: 130, ellipsis: true },
    { title: "用途标签", dataIndex: "roomUsageLabel", width: 130, ellipsis: true },
    { title: "入住人数", dataIndex: "roomCapacity", width: 100, render: (value) => value ?? "-" },
    {
      title: "关联图片",
      width: 168,
      render: (_, item) => (
        <SkuRoomImagesCell
          skuName={item.skuName}
          media={mediaBySku[item.staySkuId] ?? []}
        />
      ),
    },
    {
      title: "状态",
      width: 100,
      render: (_, item) => (
        <SkuStatusControl
          item={item}
          canManage={canManage}
          pending={runtime.mutationPending}
          onToggle={(disabled) => runtime.toggleSku(detail.staySpuId, item.staySkuId, disabled)}
        />
      ),
    },
    { title: "更新时间", width: 160, render: (_, item) => formatDateTime(item.updatedAt) },
    ...(canManage ? [{
      title: "操作",
      width: 86,
      fixed: "right" as const,
      render: (_: unknown, item: StayResourceSkuItem) => (
        <div className="stay-resource-actions">
          <IconButton label={`编辑房型 ${item.skuName}`} icon={<Pencil size={15} />} onClick={() => onEdit(detail, item)} />
        </div>
      ),
    }] : []),
  ];
  return (
    <>
      <SectionHeader title="房型维护" action={canManage ? <Button size="small" type="primary" icon={<Plus size={15} />} onClick={() => onCreate(detail)}>新增房型</Button> : null} />
      <Table<StayResourceSkuItem> rowKey="staySkuId" size="small" loading={runtime.skusLoading || runtime.mediaLoading} data={runtime.skus} columns={columns} pagination={false} noDataElement="暂无房型数据" scroll={{ x: canManage ? 1120 : 1020 }} />
    </>
  );
}

function MediaManager({ canDelete, detail, runtime, canManage, onCreate, onEdit }: {
  detail: StayResourceSpuItem;
  runtime: StayResourceManagementRuntime;
  canManage: boolean;
  canDelete: boolean;
  onCreate: (spu: StayResourceSpuItem) => void;
  onEdit: (spu: StayResourceSpuItem, item: StayResourceMediaItem) => void;
}) {
  const items = [...runtime.media].sort((left, right) => left.sortNo - right.sortNo);
  const groups = groupStayResourceMediaBySku(items, runtime.skus);
  async function move(index: number, offset: -1 | 1) {
    const target = index + offset;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target]!, reordered[index]!];
    await runtime.reorderMedia(detail.staySpuId, reordered.map((item, sortNo) => ({ stayMediaId: item.stayMediaId, sortNo })));
  }
  return (
    <>
      <SectionHeader title="展示媒体" action={canManage ? <Button size="small" type="primary" icon={<ImagePlus size={15} />} onClick={() => onCreate(detail)}>新增媒体</Button> : null} />
      {runtime.mediaLoading ? <div className="stay-resource-drawer__loading"><Spin /></div> : null}
      {!runtime.mediaLoading && !items.length ? <Empty description="暂无展示媒体" /> : null}
      <div className="stay-resource-media-groups">
        {groups.map((group) => (
          <section className="stay-resource-media-group" key={group.key} aria-label={`媒体分组 ${group.title}`}>
            <header className="stay-resource-media-group__header">
              <strong>{group.title}</strong>
              <span>{group.items.length} 张</span>
            </header>
            <div className="stay-resource-media-list">
              {group.items.map((item) => {
                const index = items.findIndex((entry) => entry.stayMediaId === item.stayMediaId);
                return (
                  <article className="stay-resource-media" key={item.stayMediaId}>
                    {item.fileUrl.match(/\.(png|jpe?g|gif|webp)(\?|$)/i) ? (
                      <a className="stay-resource-media__preview" href={resolveHttpResourceUrl(item.fileUrl)} target="_blank" rel="noreferrer" aria-label={`预览媒体 ${item.mediaName}`}>
                        <img src={resolveHttpResourceUrl(item.fileUrl)} alt={item.mediaName} loading="lazy" />
                      </a>
                    ) : (
                      <a className="stay-resource-media__file" href={resolveHttpResourceUrl(item.fileUrl)} target="_blank" rel="noreferrer" aria-label={`预览媒体 ${item.mediaName}`}>附件</a>
                    )}
                    <div className="stay-resource-media__body">
                      <strong>{item.mediaName}</strong>
                      <span>{MEDIA_SCENES[item.mediaScene] ?? item.mediaScene}</span>
                      <Tag color={item.mediaStatus === "valid" ? "green" : "gray"}>{item.mediaStatus}</Tag>
                      {canManage ? (
                        <div className="stay-resource-actions">
                          <IconButton label={`编辑媒体 ${item.mediaName}`} icon={<Pencil size={14} />} onClick={() => onEdit(detail, item)} />
                          <IconButton label={`上移媒体 ${item.mediaName}`} icon={<ArrowUp size={14} />} disabled={index <= 0} onClick={() => move(index, -1)} />
                          <IconButton label={`下移媒体 ${item.mediaName}`} icon={<ArrowDown size={14} />} disabled={index < 0 || index >= items.length - 1} onClick={() => move(index, 1)} />
                        </div>
                      ) : null}
                      {canDelete ? (
                          <IconButton label={`作废媒体 ${item.mediaName}`} icon={<Trash2 size={14} />} status="danger" onClick={() => confirmAction("作废媒体", `确认作废“${item.mediaName}”并保留历史元数据？`, () => runtime.obsoleteMedia(detail.staySpuId, item.stayMediaId))} />
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function GeoReviewPanel({ detail, runtime, canReview }: {
  detail: StayResourceSpuItem;
  runtime: StayResourceManagementRuntime;
  canReview: boolean;
}) {
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [verifyHint, setVerifyHint] = useState("");
  const geoStatus = detail.geoVerificationStatus ?? detail.geoStatus;
  const canSubmitReview = canReview && detail.geoVerificationStatus === "review_required";
  const canVerify = canReview && !runtime.geoMutationPending;
  const candidates = runtime.geoCandidates;
  const highlightedCandidateId = canSubmitReview
    ? selectedCandidateId
    : (detail.geoSelectedCandidateId ?? selectedCandidateId);
  const highlightedCandidate = candidates.find(
    (candidate) => candidate.candidateId === highlightedCandidateId,
  ) ?? candidates[0];
  const operationStatus = stayResourceOperationStatus(geoStatus);
  const operationResult = operationStatus === "idle" || operationStatus === "processing"
    ? undefined
    : stayResourceOperationResult(detail, highlightedCandidate, operationStatus);

  useEffect(() => {
    if (!candidates.length) {
      if (!canSubmitReview && detail.geoSelectedCandidateId) {
        setSelectedCandidateId(detail.geoSelectedCandidateId);
      }
      return;
    }
    setSelectedCandidateId((current) => {
      if (current && candidates.some((item) => item.candidateId === current)) {
        return current;
      }
      const preferred = detail.geoSelectedCandidateId;
      if (preferred && candidates.some((item) => item.candidateId === preferred)) {
        return preferred;
      }
      return candidates[0]?.candidateId ?? "";
    });
  }, [canSubmitReview, candidates, detail.geoSelectedCandidateId]);

  async function review(decision: "approve" | "reject") {
    if (!canSubmitReview) return;
    setError("");
    setVerifyHint("");
    const result = await runtime.reviewGeo(detail.staySpuId, { decision, candidateId: decision === "approve" ? selectedCandidateId : undefined, reason });
    if (!result.ok) {
      return setError(
        result.errors?.candidateId
          ?? result.errors?.reason
          ?? "住宿资源操作失败，请稍后重试。",
      );
    }
    setError("");
  }

  async function verifyAgain() {
    setError("");
    setVerifyHint("正在重新核验，高德检索可能需要数十秒，请勿重复点击…");
    const ok = await runtime.verifyGeo(detail.staySpuId);
    if (!ok) {
      setVerifyHint("");
      return;
    }
    setVerifyHint("重新核验已完成。若状态仍为待审核，请在下方选择候选后通过或驳回。");
  }
  const candidateColumns: TableColumnProps<StayResourceGeoCandidate>[] = [
    {
      title: "候选",
      width: 240,
      render: (_, item) => (
        <div className="stay-resource-name">
          <strong className="stay-resource-geo-candidate-name">
            <span>{item.name}</span>
            {item.candidateId === highlightedCandidateId ? <Tag color="red">已选</Tag> : null}
          </strong>
          <span>{item.poiId}</span>
        </div>
      ),
    },
    { title: "标准地址", dataIndex: "address", width: 260, ellipsis: true },
    { title: "评分", width: 80, render: (_, item) => item.score?.toFixed(2) ?? "-" },
    { title: "资格", width: 110, render: (_, item) => <Tag color={item.qualified ? "green" : "orange"}>{item.qualified ? "合格" : "自动未通过"}</Tag> },
    { title: "判定原因", dataIndex: "qualificationReason", width: 180, ellipsis: true },
    ...(canSubmitReview ? [{ title: "选择", width: 76, render: (_: unknown, item: StayResourceGeoCandidate) => <Button type={selectedCandidateId === item.candidateId ? "primary" : "outline"} size="mini" aria-label={`选择 GEO 候选 ${item.name}`} icon={<MapPin size={14} />} onClick={(event) => { event.stopPropagation(); setSelectedCandidateId(item.candidateId); }} /> }] : []),
  ];
  const reviewColumns: TableColumnProps<StayResourceGeoReviewAuditItem>[] = [
    { title: "时间", width: 160, render: (_, item) => formatDateTime(item.createdAt) },
    {
      title: "审核动作",
      width: 110,
      render: (_, item) => (
        <Tag color={item.reviewAction === "reject" ? "red" : item.reviewAction === "approve" ? "green" : "blue"}>
          {geoReviewActionLabel(item.reviewAction)}
        </Tag>
      ),
    },
    { title: "审核人", width: 90, render: (_, item) => item.createdByName || "-" },
    { title: "候选 ID", width: 180, ellipsis: true, render: (_, item) => item.candidateId ?? "-" },
    { title: "审核备注", dataIndex: "reviewNote", width: 220, ellipsis: true },
    { title: "审核前", width: 280, ellipsis: true, render: (_, item) => geoSnapshotText(item.beforeGeo) },
    { title: "审核后", width: 280, ellipsis: true, render: (_, item) => geoSnapshotText(item.afterGeo) },
  ];
  return (
    <div className="stay-resource-geo">
      <SectionHeader title="最终定位与核验" action={canReview ? (
        <Button
          size="small"
          type="primary"
          loading={runtime.geoMutationPending}
          disabled={!canVerify}
          icon={<RefreshCw size={15} />}
          onClick={() => void verifyAgain()}
        >
          {geoStatus === "unverified" ? "执行 GEO" : geoStatus === "pending" ? "核验中" : "重新核验"}
        </Button>
      ) : null} />
      <div className="stay-resource-geo__summary">
        <GeoOperationPanel
          status={operationStatus}
          result={operationResult}
          onRetry={() => void verifyAgain()}
        />
        <div className="stay-resource-geo__status"><span>核验状态</span><GeoStatusTag status={geoStatus} /></div>
        <FactGrid items={[
          ["POI ID", detail.geoAmapPoiId],
          ["坐标系", detail.geoCoordinateSystem],
          ["来源", geoSourceLabel(detail.geoSource)],
          ["最终坐标", detail.lng !== undefined && detail.lat !== undefined ? `${detail.lng.toFixed(6)}, ${detail.lat.toFixed(6)}` : undefined],
          ["最后核验", formatDateTime(detail.geoLastVerifiedAt)],
          ["审核人", geoReviewerName(detail, runtime.geoReviews)],
          ["审核时间", formatDateTime(detail.geoReviewedAt || runtime.geoReviews[0]?.createdAt)],
          ["状态原因", detail.geoFailureReason],
        ]} />
        {verifyHint ? <div className="stay-resource-geo-verify-hint" role="status">{verifyHint}</div> : null}
        {error || runtime.mutationErrorMessage ? (
          <div className="stay-resource-form-error" role="alert">{error || runtime.mutationErrorMessage}</div>
        ) : null}
      </div>
      <StayResourceGeoCandidateMap
        detail={detail}
        candidates={candidates}
        selectedCandidateId={highlightedCandidateId}
      />
      <SectionHeader title={`候选记录 ${candidates.length}`} hint={runtime.geoCandidatesUnavailable ? "候选能力尚未部署或暂不可用" : undefined} />
      <Table<StayResourceGeoCandidate>
        rowKey="candidateId"
        size="small"
        loading={runtime.geoCandidatesLoading}
        data={candidates}
        columns={candidateColumns}
        pagination={{
          pageSize: 10,
          size: "small",
          hideOnSinglePage: true,
          showTotal: true,
        }}
        noDataElement="暂无 GEO 候选"
        scroll={{ x: canSubmitReview ? 920 : 850 }}
        rowClassName={(item) => (
          item.candidateId === highlightedCandidateId ? "stay-resource-geo-candidate-row--selected" : ""
        )}
        onRow={(item) => (
          canSubmitReview
            ? { onClick: () => setSelectedCandidateId(item.candidateId) }
            : {}
        )}
      />
      {canSubmitReview ? (
        <div className="stay-resource-review-bar" aria-label="GEO 人工审核操作">
          <strong>人工审核</strong>
          <span>请选择上方候选后通过，或直接驳回本次核验结果。</span>
          {candidates.length === 0 ? (
            <span>
              当前没有可选择的候选。若原因是候选过多，请完善酒店名称与详细地址后重新核验；也可直接驳回。
            </span>
          ) : null}
          <Input.TextArea aria-label="GEO 审核原因" placeholder="审核原因（驳回时建议填写）" autoSize={{ minRows: 2, maxRows: 4 }} value={reason} onChange={setReason} />
          {error || runtime.mutationErrorMessage ? <div className="stay-resource-form-error" role="alert">{error || runtime.mutationErrorMessage}</div> : null}
          <div className="stay-resource-review-bar__actions">
            <Button type="primary" icon={<Check size={15} />} loading={runtime.geoMutationPending} disabled={!selectedCandidateId || candidates.length === 0} onClick={() => review("approve")}>通过所选候选</Button>
            <Button status="danger" icon={<XCircle size={15} />} loading={runtime.geoMutationPending} onClick={() => review("reject")}>驳回</Button>
          </div>
        </div>
      ) : null}
      <SectionHeader title={`审核历史 ${runtime.geoReviews.length}`} hint="每次自动通过、人工通过或驳回都会追加一条，可多条累计" />
      {runtime.geoReviewsUnavailable ? (
        <div className="stay-resource-error" role="alert">GEO 审核历史加载失败，请稍后重试</div>
      ) : (
        <Table<StayResourceGeoReviewAuditItem>
          rowKey="auditId"
          size="small"
          loading={runtime.geoReviewsLoading}
          data={runtime.geoReviews}
          columns={reviewColumns}
          pagination={false}
          noDataElement="暂无 GEO 审核历史"
          scroll={{ x: 1320 }}
        />
      )}
    </div>
  );
}

function stayResourceOperationStatus(status: string): GeoOperationStatus {
  if (status === "pending") return "processing";
  if (status === "resolved") return "confirmed";
  if (status === "failed") return "retryable_failure";
  if (status === "review_required" || status === "rejected") return "needs_correction";
  return "idle";
}

function stayResourceOperationResult(
  detail: StayResourceSpuItem,
  candidate: StayResourceGeoCandidate | undefined,
  status: Exclude<GeoOperationStatus, "idle" | "processing">,
): GeoBusinessVerifyResult {
  const reason = detail.geoFailureReason ?? candidate?.qualificationReason ?? "";
  return {
    scenario: "stay_resource",
    status,
    conclusion: candidate?.qualificationReason ?? reason,
    llmConclusion: candidate?.qualificationReason ?? reason,
    amapStandardAddress: candidate?.address ?? detail.address ?? "",
    province: candidate?.province ?? detail.province,
    city: candidate?.city ?? detail.city,
    district: candidate?.district ?? detail.district ?? "",
    longitude: detail.lng ?? candidate?.lng,
    latitude: detail.lat ?? candidate?.lat,
    coordinateSystem: detail.geoCoordinateSystem ?? candidate?.coordinateSystem ?? "GCJ-02",
    confidenceScore: candidate?.score ?? (status === "confirmed" ? 1 : 0),
    correctionReason: status === "confirmed" ? "" : reason,
    retryable: status === "retryable_failure",
    candidates: [],
  };
}

function SupplierManager({ detail, runtime, canManage, onCreate, onEdit }: {
  detail: StayResourceSpuItem;
  runtime: StayResourceManagementRuntime;
  canManage: boolean;
  onCreate: (spu: StayResourceSpuItem) => void;
  onEdit: (spu: StayResourceSpuItem, item: StayResourceSupplierItem) => void;
}) {
  const [reviewItem, setReviewItem] = useState<StayResourceSupplierItem>();
  const columns: TableColumnProps<StayResourceSupplierItem>[] = [
    { title: "供应商", width: 220, ellipsis: true, render: (_, item) => <div className="stay-resource-name"><strong>{item.supplierName}</strong><span>{item.supplierId}</span></div> },
    { title: "覆盖房型", width: 150, ellipsis: true, render: (_, item) => item.skuName ?? "酒店级" },
    { title: "采购渠道", width: 130, render: (_, item) => optionLabel(stayProcurementChannelOptions, item.procurementChannelType) },
    { title: "合作状态", dataIndex: "cooperationStatus", width: 110 },
    { title: "审核状态", dataIndex: "reviewStatus", width: 100 },
    { title: "有效期", width: 190, render: (_, item) => `${item.validFrom ?? "-"} 至 ${item.validTo ?? "-"}` },
    { title: "证据", width: 180, ellipsis: true, render: (_, item) => item.evidenceSummary ?? item.evidenceId ?? "-" },
    ...(canManage ? [{ title: "操作", width: 140, fixed: "right" as const, render: (_: unknown, item: StayResourceSupplierItem) => <div className="stay-resource-actions"><IconButton label={`编辑供应能力 ${item.supplierName}`} icon={<Pencil size={14} />} onClick={() => onEdit(detail, item)} /><IconButton label={`审核供应能力 ${item.supplierName}`} icon={<Check size={14} />} onClick={() => setReviewItem(item)} /><IconButton label={`停用供应能力 ${item.supplierName}`} icon={<PowerOff size={14} />} status="danger" onClick={() => confirmAction("停用供应能力", `确认停用“${item.supplierName}”的当前供应能力？`, () => runtime.deactivateSupplier(detail.staySpuId, item))} /></div> }] : []),
  ];
  return (
    <>
      <SectionHeader title="统一供应能力" action={canManage ? <Button type="primary" size="small" icon={<Plus size={15} />} onClick={() => onCreate(detail)}>新增供应能力</Button> : null} />
      <Table<StayResourceSupplierItem> rowKey="capabilityId" size="small" loading={runtime.suppliersLoading} data={runtime.suppliers} columns={columns} pagination={false} noDataElement="暂无供应关系" scroll={{ x: canManage ? 1180 : 1000 }} />
      {reviewItem ? (
        <div className="stay-resource-review-bar">
          <strong>审核：{reviewItem.supplierName} · {reviewItem.skuName ?? "酒店级"}</strong>
          <span>审核通过需具备有效证据；终止后将不再用于资源合作。</span>
          <div className="stay-resource-review-bar__actions">
            <Button type="primary" icon={<Check size={15} />} loading={runtime.mutationPending} onClick={async () => { if (await runtime.reviewSupplier(detail.staySpuId, reviewItem, { decision: "approve" })) setReviewItem(undefined); }}>审核通过</Button>
            <Button status="danger" icon={<XCircle size={15} />} loading={runtime.mutationPending} onClick={async () => { if (await runtime.reviewSupplier(detail.staySpuId, reviewItem, { decision: "reject" })) setReviewItem(undefined); }}>驳回</Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function SectionHeader({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return <header className="stay-resource-section-header"><div><h2>{title}</h2>{hint ? <span>{hint}</span> : null}</div>{action}</header>;
}

function Fact({ label, value, wide = false }: { label: string; value?: ReactNode; wide?: boolean }) {
  return <div className={wide ? "stay-resource-fact stay-resource-fact--wide" : "stay-resource-fact"}><dt>{label}</dt><dd>{value || "-"}</dd></div>;
}

function FactGrid({ items }: { items: Array<[string, string | undefined]> }) {
  return <dl className="stay-resource-facts">{items.map(([label, value]) => <Fact key={label} label={label} value={value} />)}</dl>;
}

function GeoStatusTag({ status }: { status: string }) {
  const current = GEO_STATUS[status] ?? { label: status, color: "gray" };
  return <Tag color={current.color}>{current.label}</Tag>;
}

function CountLink({ label, count, onClick }: { label: string; count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      className="stay-resource-link stay-resource-count-link"
      aria-label={`查看${label} ${count}`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{count}</strong>
    </button>
  );
}

function GeoStatusAction({ item, canReview, pending, onOpen, onVerify }: {
  item: StayResourceSpuItem;
  canReview: boolean;
  pending: boolean;
  onOpen: () => void;
  onVerify: () => Promise<boolean>;
}) {
  const status = item.geoVerificationStatus ?? item.geoStatus;
  const current = GEO_STATUS[status] ?? { label: status, color: "gray" };
  if (status === "pending" && pending) {
    return (
      <Button
        type="text"
        size="small"
        className="stay-resource-geo-action"
        aria-label={`GEO 核验中 ${item.hotelName}`}
        disabled
      >
        <Tag color={current.color}>{current.label}</Tag>
      </Button>
    );
  }
  if (status === "resolved" || status === "review_required" || status === "failed") {
    const label = status === "review_required"
      ? `打开 GEO 审核 ${item.hotelName}`
      : status === "failed"
        ? `打开 GEO 详情 ${item.hotelName}`
        : `查看 GEO 详情 ${item.hotelName}`;
    return (
      <Button
        type="text"
        size="small"
        className="stay-resource-geo-action"
        aria-label={label}
        onClick={onOpen}
      >
        <Tag color={current.color}>{current.label}</Tag>
      </Button>
    );
  }
  const retryLabel = status === "rejected" ? "重新执行 GEO" : "执行 GEO";
  return (
    <Button
      type="text"
      size="small"
      className="stay-resource-geo-action"
      aria-label={`${retryLabel} ${item.hotelName}`}
      loading={pending}
      disabled={!canReview || pending}
      onClick={() => void onVerify()}
    >
      <Tag color={current.color}>{current.label}</Tag>
    </Button>
  );
}

function ResourceStatusControl({ item, canManage, pending, onToggle }: {
  item: StayResourceSpuItem;
  canManage: boolean;
  pending: boolean;
  onToggle: (disabled: boolean) => Promise<boolean>;
}) {
  return (
    <StatusControl
      active={!item.isDisabled && item.status === "active"}
      disabled={!canManage || pending}
      activeLabel={`停用酒店 ${item.hotelName}`}
      inactiveLabel={`启用酒店 ${item.hotelName}`}
      confirmText={`确认停用“${item.hotelName}”？`}
      onToggle={onToggle}
    />
  );
}

function SkuStatusControl({ item, canManage, pending, onToggle }: {
  item: StayResourceSkuItem;
  canManage: boolean;
  pending: boolean;
  onToggle: (disabled: boolean) => Promise<boolean>;
}) {
  return (
    <StatusControl
      active={!item.isDisabled && item.status === "active"}
      disabled={!canManage || pending}
      activeLabel={`停用房型 ${item.skuName}`}
      inactiveLabel={`启用房型 ${item.skuName}`}
      confirmText={`确认停用房型“${item.skuName}”？`}
      onToggle={onToggle}
    />
  );
}

function StatusControl({ active, disabled, activeLabel, inactiveLabel, confirmText, onToggle }: {
  active: boolean;
  disabled: boolean;
  activeLabel: string;
  inactiveLabel: string;
  confirmText: string;
  onToggle: (disabled: boolean) => Promise<boolean>;
}) {
  const button = (
    <Button
      type="text"
      size="small"
      className="stay-resource-status-button"
      aria-label={active ? activeLabel : inactiveLabel}
      disabled={disabled}
      onClick={() => {
        if (!active) void onToggle(false);
      }}
    >
      <Tag color={active ? "green" : "gray"}>{active ? "启用" : "停用"}</Tag>
    </Button>
  );
  if (!active || disabled) return button;
  return (
    <Popconfirm
      title={confirmText}
      okText="停用"
      cancelText="取消"
      okButtonProps={{ status: "danger" }}
      onOk={() => onToggle(true)}
    >
      {button}
    </Popconfirm>
  );
}

function IconButton({ label, icon, status, disabled, onClick }: { label: string; icon: ReactNode; status?: "danger"; disabled?: boolean; onClick: () => void }) {
  return <Tooltip content={label}><Button type="text" size="mini" status={status} disabled={disabled} aria-label={label} icon={icon} onClick={onClick} /></Tooltip>;
}

function confirmAction(title: string, content: string, action: () => Promise<unknown>) {
  Modal.confirm({ title, content, okButtonProps: { status: "danger" }, onOk: action });
}

function locationText(item: StayResourceSpuItem): string {
  return [item.country, item.province, item.city, item.district].filter(Boolean).join(" / ");
}

function regionText(item: StayResourceSpuItem): string {
  return [item.province, item.city, item.district, item.address].filter(Boolean).join(" ");
}

function formatDateTime(value?: string): string {
  if (!value) return "-";
  const normalized = value.replace("T", " ");
  return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
}

function geoSourceLabel(source?: string): string | undefined {
  if (!source) return undefined;
  const labels: Record<string, string> = {
    amap_poi: "高德自动",
    amap_geocode: "高德地理编码",
    manual_review: "人工审核",
    legacy_verified: "存量已核验",
  };
  return labels[source] ?? source;
}

/** 将筛选下拉挂到 body，避免搜索后弹层被表格区域挡住。 */
function filterPopupContainer() {
  return document.body;
}

function geoReviewerName(
  detail: StayResourceSpuItem,
  reviews: StayResourceGeoReviewAuditItem[],
): string | undefined {
  if (detail.geoReviewedBy) {
    const matched = reviews.find((item) => String(item.createdBy) === String(detail.geoReviewedBy));
    return matched?.createdByName;
  }
  return reviews[0]?.createdByName;
}

function geoVerificationStatusLabel(status?: string): string {
  if (!status) return "";
  return GEO_STATUS[status]?.label ?? status;
}

function legacyReviewStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: "待审核",
    approved: "已通过",
    rejected: "已驳回",
  };
  return labels[status] ?? status;
}

function geoReviewActionLabel(action: StayResourceGeoReviewAuditItem["reviewAction"]): string {
  return {
    auto_approve: "自动通过",
    approve: "人工通过",
    reject: "人工驳回",
  }[action];
}

function geoSnapshotText(snapshot: Record<string, unknown>): string {
  const address = typeof snapshot.address === "string" ? snapshot.address : "";
  const lng = typeof snapshot.lng === "string" || typeof snapshot.lng === "number"
    ? String(snapshot.lng)
    : "";
  const lat = typeof snapshot.lat === "string" || typeof snapshot.lat === "number"
    ? String(snapshot.lat)
    : "";
  const status = typeof snapshot.geo_verification_status === "string"
    ? geoVerificationStatusLabel(snapshot.geo_verification_status)
    : "";
  const source = typeof snapshot.geo_source === "string"
    ? geoSourceLabel(snapshot.geo_source)
    : "";
  return [address, lng && lat ? `${lng}, ${lat}` : "", status, source].filter(Boolean).join(" · ") || "-";
}

function optionLabel(options: ReadonlyArray<{ value: string; label: string }>, value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}
